import React from 'react';
import { useDispatch } from 'react-redux';

import { RunIdentificationPair } from '@neptune/shared/entity-domain';

import {
  performArchiveEntity,
  performBulkArchiveEntities,
  performBulkUnarchiveEntities,
  performUnarchiveEntity,
} from './archive-entity';

export function useArchiveEntity(
  projectIdentifier?: string,
  removeFromCompare?: ({
    identificationPairsToRemove,
    allowedToRemovePair,
  }: {
    identificationPairsToRemove: RunIdentificationPair[];
    allowedToRemovePair?: boolean;
  }) => void,
) {
  const dispatch = useDispatch();

  const bulkArchiveEntities = React.useCallback(
    async (shortIds: string[]) => {
      if (!projectIdentifier) {
        return;
      }

      await performBulkArchiveEntities(projectIdentifier, shortIds, dispatch);
      removeFromCompare?.({
        identificationPairsToRemove: shortIds.map((shortId) => ({
          runId: shortId,
          experimentName: undefined,
        })),
        allowedToRemovePair: true,
      });
    },
    [dispatch, projectIdentifier, removeFromCompare],
  );

  const bulkUnarchiveEntities = React.useCallback(
    async (shortIds: string[]) => {
      if (!projectIdentifier) {
        return;
      }

      return performBulkUnarchiveEntities(projectIdentifier, shortIds, dispatch);
    },
    [dispatch, projectIdentifier],
  );

  const archiveEntity = React.useCallback(
    async (shortId: string) => {
      if (!projectIdentifier) {
        return;
      }

      await performArchiveEntity(projectIdentifier, shortId, dispatch);
      removeFromCompare?.({
        identificationPairsToRemove: [{ runId: shortId, experimentName: undefined }],
      });
    },
    [dispatch, projectIdentifier, removeFromCompare],
  );

  const unarchiveEntity = React.useCallback(
    async (shortId: string) => {
      if (!projectIdentifier) {
        return;
      }

      return performUnarchiveEntity(projectIdentifier, shortId, dispatch);
    },
    [dispatch, projectIdentifier],
  );

  return { bulkArchiveEntities, bulkUnarchiveEntities, archiveEntity, unarchiveEntity };
}
